import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';

const WaffleGrid = ({ stablecoinPercentage, totalTiles = 100 }) => {
  const [hoveredTile, setHoveredTile] = useState(null);
  
  const tiles = useMemo(() => {
    // Calculate exact tiles needed for accurate fractional representation
    const exactTiles = (stablecoinPercentage / 100) * totalTiles;
    const fullTiles = Math.floor(exactTiles);
    const frac = exactTiles - fullTiles;
    
    // Generate tiles with bottom-up filling order
    // Bottom row (indices 90-99), then next row up (80-89), etc.
    const tileOrder = [];
    for (let row = 9; row >= 0; row--) {
      for (let col = 0; col < 10; col++) {
        tileOrder?.push(row * 10 + col);
      }
    }
    
    return Array.from({ length: totalTiles }, (_, visualIndex) => {
      const fillIndex = tileOrder?.indexOf(visualIndex);
      
      let type, fillAmount;
      if (fillIndex < fullTiles) {
        type = 'stablecoin';
        fillAmount = 1; // Fully filled
      } else if (fillIndex === fullTiles && frac > 0) {
        type = 'partial';
        fillAmount = frac; // Partially filled
      } else {
        type = 'm2';
        fillAmount = 0; // Empty (gray)
      }
      
      return {
        id: visualIndex,
        type,
        fillAmount,
        color: type === 'm2' ? 'bg-[#D9D9D9]' : 'bg-[#D08A2A]'
      };
    });
  }, [stablecoinPercentage, totalTiles]);

  const handleTileHover = (tile) => {
    setHoveredTile(tile);
  };

  const handleTileLeave = () => {
    setHoveredTile(null);
  };

  return (
    <div className="relative">
      <div className="bg-card rounded-lg p-4 md:p-6 lg:p-8 shadow-sm border border-border">
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <h2 className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground">
            Stablecoin to M2 Ratio Visualization
          </h2>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-full">
            <Icon name="TrendingUp" size={16} color="var(--color-primary)" />
            <span className="text-xs md:text-sm font-medium text-primary">
              Live Data
            </span>
          </div>
        </div>

        <div 
          className="grid gap-1.5 md:gap-2 lg:gap-2.5 mx-auto"
          style={{
            gridTemplateColumns: 'repeat(10, minmax(0, 1fr))',
            maxWidth: '100%'
          }}
          role="img"
          aria-label={`Waffle chart showing ${stablecoinPercentage?.toFixed(2)}% stablecoins versus ${(100 - stablecoinPercentage)?.toFixed(2)}% M2 money supply`}
        >
          {tiles?.map((tile) => (
            <div
              key={tile?.id}
              className={`
                relative
                rounded 
                transition-all 
                duration-300 
                cursor-pointer
                hover:scale-110
                hover:shadow-md
                ${hoveredTile?.id === tile?.id ? 'ring-2 ring-primary ring-offset-2' : ''}
                ${tile?.type === 'm2' ? 'bg-[#D9D9D9]' : ''}
              `}
              style={{
                aspectRatio: '1/1',
                overflow: 'hidden'
              }}
              onMouseEnter={() => handleTileHover(tile)}
              onMouseLeave={handleTileLeave}
              role="presentation"
            >
              {tile?.type === 'stablecoin' && (
                <div 
                  className="absolute bottom-0 left-0 right-0 bg-[#D08A2A]"
                  style={{ height: '100%' }}
                />
              )}
              {tile?.type === 'partial' && (
                <>
                  <div className="absolute inset-0 bg-[#D9D9D9]" />
                  <div 
                    className="absolute bottom-0 left-0 right-0 bg-[#D08A2A]"
                    style={{ height: `${tile?.fillAmount * 100}%` }}
                  />
                </>
              )}
            </div>
          ))}
        </div>

        <div className="mt-4 md:mt-6 flex items-center justify-center gap-2 text-xs md:text-sm text-muted-foreground">
          <Icon name="Grid3x3" size={16} color="var(--color-muted-foreground)" />
          <span>Each square represents 1% of the total US M2 supply</span>
        </div>
      </div>
      {hoveredTile && (
        <div 
          className="fixed z-50 bg-popover text-popover-foreground px-4 py-3 rounded-lg shadow-lg border border-border pointer-events-none"
          style={{
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)'
          }}
        >
          <div className="flex items-center gap-2 mb-1">
            <Icon 
              name={hoveredTile?.type === 'm2' ? 'Landmark' : 'Coins'} 
              size={16} 
              color="var(--color-foreground)" 
            />
            <span className="text-sm font-semibold">
              {hoveredTile?.type === 'm2' ? 'M2 Money Supply' : hoveredTile?.type === 'partial' ? 'Partial Stablecoin' : 'Stablecoin'}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            Tile #{hoveredTile?.id + 1} of {totalTiles}
            {hoveredTile?.type === 'partial' && ` (${(hoveredTile?.fillAmount * 100)?.toFixed(1)}% filled)`}
          </p>
        </div>
      )}
    </div>
  );
};

export default WaffleGrid;